import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { CartComponentStore, CartEntityItem } from 'cart-data';
import { IdOption, IdOptionCollection, ItemId, ProductTypes } from 'products-data';
import { ItemComponentBase } from 'products-ui';
import { CustomLength } from './types';
import { WireCutCustomLengthSelectorViewComponent } from './views/wire-cut-custom-length-selector-view/wire-cut-custom-length-selector-view.component';

@Component({
  selector: 'ideal-wire-cut-custom-length-selector',
  imports: [WireCutCustomLengthSelectorViewComponent],
  templateUrl: './wire-cut-custom-length-selector.component.html',
  styleUrl: './wire-cut-custom-length-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WireCutCustomLengthSelectorComponent extends ItemComponentBase {
  private readonly _store = inject(CartComponentStore);
  protected readonly isLoading = computed(() => this._store.cartLoading() || this._store.cartPricesLoading());

  protected readonly items = computed(() => {
    const id = this.itemId();
    if (!id) return [];
    return this._store
      .cartItems()
      .filter((item) => item.id.lineCode === id.lineCode && item.id.partNumber === id.partNumber && item.id.options.has('Z'));
  });

  private readonly _conflictedItems = signal<CustomLength[]>([]);

  protected readonly customLengths = computed(() => {
    const cartLengths = this._cartItemsToCustomLengths(this.items());
    const conflicted = this._conflictedItems();
    return [...cartLengths, ...conflicted];
  });

  private _isSyncingToCart = false;

  constructor() {
    super();

    effect(() => {
      const cartItems = this.items();

      if (this._isSyncingToCart) {
        return;
      }

      const cartLengthValues = new Set(
        cartItems
          .map((item) => {
            const lengthStr = item.id.options.value('Z');
            if (!lengthStr) return null;
            const length = parseInt(lengthStr, 10);
            return isNaN(length) || length <= 0 ? null : length;
          })
          .filter((l): l is number => l !== null),
      );

      const conflicted = this._conflictedItems();
      const itemsToPromote: CustomLength[] = [];
      const remainingConflicted: CustomLength[] = [];

      for (const item of conflicted) {
        if (!cartLengthValues.has(item.length)) {
          const existing = itemsToPromote.find((i) => i.length === item.length);
          if (!existing) {
            itemsToPromote.push(item);
          } else {
            remainingConflicted.push(item);
          }
        } else {
          remainingConflicted.push(item);
        }
      }

      if (itemsToPromote.length > 0 || remainingConflicted.length !== conflicted.length) {
        if (itemsToPromote.length > 0) {
          this._isSyncingToCart = true;
        }

        this._conflictedItems.set(remainingConflicted);

        if (itemsToPromote.length > 0) {
          for (const item of itemsToPromote) {
            const itemId = this._createItemIdWithLength(item.length);
            this._store.updateItemQuantity(itemId, item.quantity);
          }
          setTimeout(() => {
            this._isSyncingToCart = false;
          }, 100);
        }
      }
    });
  }

  protected onLengthsChange(lengths: CustomLength[]): void {
    if (this._isSyncingToCart) return;
    this._syncFormToCartAndConflicted(lengths);
  }

  private _cartItemsToCustomLengths(cartItems: CartEntityItem[]): CustomLength[] {
    const lengths: CustomLength[] = [];

    for (const item of cartItems) {
      const lengthStr = item.id.options.value('Z');

      if (lengthStr) {
        const length = parseInt(lengthStr, 10);
        if (!isNaN(length) && length > 0 && item.quantity > 0) {
          lengths.push({ length, quantity: item.quantity });
        }
      }
    }

    return lengths;
  }

  private _createItemIdWithLength(length: number): ItemId {
    const baseId = this.itemId();
    if (!baseId) {
      throw new Error('Cannot create ItemId: base itemId is not defined');
    }
    const options = new IdOptionCollection([new IdOption('Z', length.toString())]);
    return new ItemId(baseId.lineCode, baseId.partNumber, ProductTypes.Wire, options);
  }

  private _syncFormToCart(customLengths: CustomLength[]): void {
    this._isSyncingToCart = true;

    try {
      const currentCartItems = this.items();
      const cartItemsByLength = new Map<number, CartEntityItem>();
      for (const item of currentCartItems) {
        const lengthStr = item.id.options.value('Z');
        if (lengthStr) {
          const length = parseInt(lengthStr, 10);
          if (!isNaN(length)) {
            cartItemsByLength.set(length, item);
          }
        }
      }

      const formLengths = new Set(customLengths.map((i) => i.length));

      for (const customLength of customLengths) {
        const itemId = this._createItemIdWithLength(customLength.length);
        const existingItem = cartItemsByLength.get(customLength.length);

        if (!existingItem || existingItem.quantity !== customLength.quantity) {
          this._store.updateItemQuantity(itemId, customLength.quantity);
        }
      }

      for (const [length, cartItem] of cartItemsByLength.entries()) {
        if (!formLengths.has(length)) {
          this._store.updateItemQuantity(cartItem.id, 0);
        }
      }
    } finally {
      setTimeout(() => {
        this._isSyncingToCart = false;
      }, 100);
    }
  }

  private _syncFormToCartAndConflicted(customLengths: CustomLength[]): void {
    const seenLengths = new Map<number, CustomLength>();
    const conflictedItems: CustomLength[] = [];

    for (const item of customLengths) {
      const existing = seenLengths.get(item.length);
      if (!existing) {
        seenLengths.set(item.length, item);
      } else {
        conflictedItems.push(item);
      }
    }

    this._conflictedItems.set(conflictedItems);

    const cartItems = Array.from(seenLengths.values());
    this._syncFormToCart(cartItems);
  }
}
